<template>
  <div class="message-info">
    <!-- 送信者 -->
    <el-row class="py sender text-xs">
      <el-col :span="5" class="middle-label-align-top">
        <input-label :required="isEdit">送信者</input-label>
      </el-col>
      <el-col :span="15" class="pr">
        <address-by-department :addresses="[senderAddress]" />
      </el-col>
    </el-row>
    <!-- 宛先 -->
    <el-row class="py receiver text-xs">
      <el-col :span="5" class="middle-label-align-top">
        <input-label :required="isEdit">宛先</input-label>
      </el-col>
      <el-col :span="15" class="pr">
        <el-form-item prop="message_address" class="message_address">
          <address-by-department :addresses="form.message_address" />
        </el-form-item>
      </el-col>
      <el-col :span="4" class="address-select">
        <address-select-dialog
          v-if="isAddressVisible"
          :modelValue="form.message_address"
          :fixedDepartment="fixedDepartmentId"
          @update:modelValue="updateMessageAddress($event)"
          :isEdit="canAddressEdit"
        />
      </el-col>
    </el-row>
    <!-- 添付ファイル -->
    <el-row class="py" v-if="canAttached">
      <el-col :span="5" class="other_files_padding text-xs">
        <input-label :required="false">添付ファイル</input-label>
      </el-col>
      <el-col :span="19">
        <message-attache-files v-model="form.attached_file" :isEdit="isEdit" ref="attacheFileRef" />
      </el-col>
    </el-row>
    <!-- メッセージ本文 -->
    <el-row class="py">
      <el-col :span="5" class="align-middle pb text-xs">
        <input-label :required="isEdit">メッセージ</input-label>
      </el-col>
      <el-col :span="16" class="pb">
        <template-select-dialog
          :requestHeaderId="form.request_header"
          :messageType="form.message_type"
          :isEdit="isEdit"
          @update:template="form.body = $event"
        />
      </el-col>
      <el-col :span="24">
        <el-form-item prop="body" class="body">
          <jr-textarea :rows="10" v-model="form.body" :isEdit="isEdit" size="small" />
        </el-form-item>
      </el-col>
    </el-row>
    <!-- 備考 -->
    <el-row class="py" v-if="canRemark && form.remark != null">
      <el-col :span="24" class="pb remark-title text-xs">
        <input-label v-if="isApplicantSend" :required="false">備考(協議先には表示されません)</input-label>
        <input-label v-else :required="false">備考</input-label>
      </el-col>
      <el-col :span="24">
        <el-form-item prop="remark" class="remark">
          <jr-textarea :rows="5" v-model="form.remark" :isEdit="isEdit" size="small" />
        </el-form-item>
      </el-col>
    </el-row>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch, PropType } from 'vue'
import { useStore, Store } from '@/store'

import InputLabel from '@/components/inputs/InputLabel.vue'
import JrTextarea from '@/components/jr-parts/JrTextarea.vue'
import AddressByDepartment from '@/components/layout/AddressByDepartment.vue'
import AddressSelectDialog from '@/components/dialogs/AddressSelectDialog.vue'
import TemplateSelectDialog from '@/components/dialogs/TemplateSelectDialog.vue'
import MessageAttacheFiles from '@/components/inputs/MessageAttacheFiles.vue'

import { MessageForm } from '@/types/forms'
import { RequestTypes } from '@/constants/enums'
import { RequestHeaderDetail, Message, MessageAddress } from '@/api'
import MessageTypeLogic from '@/services/messageTypeLogic'
import { getSenderFromMessage } from '@/services/messageAddress'

/**
 * メッセージ画面
 */
export default defineComponent({
  name: 'Message',
  emits: ['update:modelValue', 'update:attacheFileRef'],
  components: {
    InputLabel,
    AddressByDepartment,
    AddressSelectDialog,
    TemplateSelectDialog,
    JrTextarea,
    MessageAttacheFiles,
  },
  props: {
    modelValue: {
      type: Object as PropType<MessageForm>,
      required: true,
    },
    requestHeader: {
      type: Object as PropType<RequestHeaderDetail>,
      required: true,
    },
    isEdit: {
      type: Boolean,
      required: true,
    },
    isAddressEdit: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  setup(props, context) {
    const store = useStore() as Store

    const attacheFileRef = ref<InstanceType<typeof MessageAttacheFiles>>()
    const form = ref<MessageForm>(props.modelValue)

    // 宛先ダイアログのopenが可能か
    const isAddressVisible = computed(() => {
      //宛先に協議先が含まれるか
      const isReceiverApplicant = props.modelValue.message_address.some((address) => address.is_applicant)
      // 宛先に協議者が含まれない場合は、表示する。
      return !isReceiverApplicant
    })

    // 宛先の編集が可能か
    const canAddressEdit = computed(() => {
      //コンポーネントの編集が不可の場合は編集不可
      if (!props.isEdit) {
        return false
      }
      return props.isAddressEdit
    })

    // 送信者情報
    const senderAddress = computed(() => {
      if (props.isEdit) {
        // 編集時はログイン者の情報
        return store.getters['user/getSenderMessageAddress']
      } else {
        // 閲覧時はformの内容から送信者を返す
        return getSenderFromMessage(props.modelValue as Message)
      }
    })

    // メッセージ種別の受信者が協議先か
    const isApplicantSend = ref<boolean>(false)

    /**
     * 宛先の更新
     */
    const updateMessageAddress = (ma: MessageAddress[]) => {
      if (form.value != null) {
        form.value.message_address.splice(0)
        form.value.message_address.push(...ma)
      }
    }

    // 添付ファイル可能か
    const canAttached = ref<boolean>(false)
    // 備考設定可能か
    const canRemark = ref<boolean>(false)
    // 固定保守区
    const fixedDepartmentId = ref<number | null>(null)

    // formが変更されたらemitする
    watch(
      () => form,
      () => {
        context.emit('update:modelValue', form.value)
      },
      { immediate: true, deep: true }
    )

    // modelValueが変更された時
    watch(
      () => props.modelValue,
      () => {
        // formに格納
        form.value = props.modelValue

        // 種別毎設定
        const logic = new MessageTypeLogic(props.modelValue.message_type, props.modelValue.request_header)
        canAttached.value = logic.canAttachedFile()
        canRemark.value = logic.canRemark()
        isApplicantSend.value = logic.isApplicantReceiveType()
        fixedDepartmentId.value = logic.getFixedDepartmentId(props.requestHeader)
      },
      { immediate: true, deep: true }
    )
    // 添付ファイルコンポーネントが変更されたらemitして親に渡す
    watch(
      attacheFileRef,
      () => {
        context.emit('update:attacheFileRef', attacheFileRef.value)
      },
      { immediate: true }
    )

    return {
      form,
      RequestTypes,
      canAttached,
      canRemark,
      isApplicantSend,
      attacheFileRef,
      senderAddress,
      isAddressVisible,
      fixedDepartmentId,
      updateMessageAddress,
      canAddressEdit,
    }
  },
})
</script>
<style lang="scss" scoped>
.el-form-item {
  // デフォルトのdisplay:flexは、宛先ボタンが隠れるためblockに変更。
  display: block;
}
</style>
